import { DISMISSAL_REASONS, type DismissalReason } from "@esi/shared";
import { type ReactNode, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

/** How long the reasons stay offered after a dismissal (recommendation-outcomes D8). */
export const DISMISS_REASONS_MS = 8_000;

/**
 * The quiet line a dismissed or rejected recommendation settles into (openspec change
 * `recommendation-outcomes`): its status, then "Why?" and three one-tap reasons for 8 seconds or
 * until the next turn starts. Never required, never free text: the dismissal already happened.
 * The offer waits while focus is inside it, so a keyboard user is never cut off mid-choice.
 */
export function DismissReasons(props: {
  /** The settled line ("Not saved…", "Suggestion dismissed."). */
  children: ReactNode;
  /** Sends the reason; a failure is ignored (the reason is optional). */
  onReason: (reason: DismissalReason) => Promise<unknown>;
  /** False hides the offer (a new turn started, or this is an earlier decision). */
  offer?: boolean;
  className?: string;
}) {
  const { t } = useTranslation();
  const [state, setState] = useState<"asking" | "sent" | "closed">(
    props.offer === false ? "closed" : "asking",
  );
  const [focused, setFocused] = useState(false);
  const group = useRef<HTMLFieldSetElement>(null);
  const asking = state === "asking" && props.offer !== false;

  // The next turn started: the offer ends for good (it never comes back after the turn).
  useEffect(() => {
    if (props.offer === false) {
      setState((s) => (s === "asking" ? "closed" : s));
    }
  }, [props.offer]);

  useEffect(() => {
    if (!asking || focused) {
      return;
    }
    const timer = setTimeout(() => setState("closed"), DISMISS_REASONS_MS);
    return () => clearTimeout(timer);
  }, [asking, focused]);

  return (
    <div
      className={cn(
        "flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-fg-muted",
        props.className,
      )}
    >
      <p role="status">
        {props.children}
        {state === "sent" ? ` ${t("recommendations.reasonNoted")}` : null}
      </p>
      {asking ? (
        <fieldset
          ref={group}
          aria-label={t("recommendations.whyLabel")}
          className="m-0 flex min-w-0 flex-wrap items-center gap-1 border-0 p-0"
          onFocus={() => setFocused(true)}
          onBlur={(e) => {
            if (!group.current?.contains(e.relatedTarget as Node | null)) {
              setFocused(false);
            }
          }}
        >
          <span aria-hidden="true">{t("recommendations.why")}</span>
          {DISMISSAL_REASONS.map((reason) => (
            <Button
              key={reason}
              type="button"
              size="xs"
              variant="outline"
              // Small hairline chips: plainly tappable, still quieter than the card's actions.
              className="font-normal text-fg-secondary"
              onClick={() => {
                setState("sent");
                void props.onReason(reason).catch(() => {});
              }}
            >
              {t(`recommendations.reasons.${reason}`)}
            </Button>
          ))}
        </fieldset>
      ) : null}
    </div>
  );
}
